import { CoreTypes, type ObjectRecord, type Primitive } from '@vp/shared-types';
import { boxBounds, localToWorld, normalizeRect } from '@vp/core';
import type { Camera } from '../camera';
import { drawPrimitive } from '../canvas2d';
import { buildPrimitives } from '../display';
import { boxScreenCorners, HANDLE_SIZE } from './handles';
import type { Editor } from './editor';

/** Selection follows the app accent. Falls back to the base blue when no document theme is set. */
let accent = '#5e9cff';

function syncAccent(): void {
  if (typeof document === 'undefined') return;
  const v = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) accent = v;
}

function accentFade(alpha: number): string {
  const n = Number.parseInt(accent.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

function outline(ctx: CanvasRenderingContext2D, camera: Camera, o: ObjectRecord, color: string, width: number): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  if (o.type === CoreTypes.line || o.type === CoreTypes.connector || o.type === CoreTypes.pen || o.type === CoreTypes.path) {
    const pts = (o.type === CoreTypes.connector ? o.props['path'] : o.props['points']) as number[] | undefined;
    if (!pts || pts.length < 4) return;
    ctx.beginPath();
    for (let i = 0; i < pts.length; i += 2) {
      const w = localToWorld(o, { x: pts[i], y: pts[i + 1] });
      const s = camera.worldToScreen(w.x, w.y);
      if (i === 0) ctx.moveTo(s.x, s.y);
      else ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
    return;
  }
  const c = boxScreenCorners(o, camera);
  ctx.beginPath();
  ctx.moveTo(c[0].x, c[0].y);
  for (let i = 1; i < 4; i++) ctx.lineTo(c[i].x, c[i].y);
  ctx.closePath();
  ctx.stroke();
}

const NO_IMAGES = { get: () => null };

/** Draw an object that is not in the document yet (live drawing preview) exactly as the renderer would. */
function drawGhost(ctx: CanvasRenderingContext2D, camera: Camera, o: ObjectRecord, ed: Editor): void {
  const prims = buildPrimitives(o, 0, ed.scene.displayCtx);
  const z = camera.zoom;
  const c = camera.worldToScreen(o.x + o.w / 2, o.y + o.h / 2);
  ctx.save();
  ctx.translate(c.x, c.y);
  ctx.rotate(o.rotation);
  ctx.scale(z, z);
  ctx.translate(-o.w / 2, -o.h / 2);
  for (const p of prims) drawPrimitive(ctx, p, o.style.opacity ?? 1, z * camera.dpr, { images: NO_IMAGES, textLodPx: 0 });
  ctx.restore();
  ctx.globalAlpha = 1;
  ctx.setLineDash([]);
}

/** Anchors (squares), handles of the selected node and its neighbours, and the "add point" marker. */
function drawPathEdit(ctx: CanvasRenderingContext2D, camera: Camera, o: ObjectRecord, selected: number, hover: { x: number; y: number } | null): void {
  const nodes = (o.props['nodes'] as number[] | undefined) ?? [];
  const n = Math.floor(nodes.length / 6);
  const toScreen = (x: number, y: number) => {
    const w = localToWorld(o, { x, y });
    return camera.worldToScreen(w.x, w.y);
  };
  outline(ctx, camera, o, accent, 1);
  ctx.lineWidth = 1;
  for (const i of [selected - 1, selected, selected + 1]) {
    if (selected < 0 || i < 0 || i >= n) continue;
    const b = i * 6;
    const a = toScreen(nodes[b], nodes[b + 1]);
    for (const h of [2, 4]) {
      if (nodes[b + h] === nodes[b] && nodes[b + h + 1] === nodes[b + 1]) continue;
      const q = toScreen(nodes[b + h], nodes[b + h + 1]);
      ctx.strokeStyle = accent;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(q.x, q.y);
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(q.x, q.y, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
  for (let i = 0; i < n; i++) {
    const a = toScreen(nodes[i * 6], nodes[i * 6 + 1]);
    const r = 3.5;
    ctx.fillStyle = i === selected ? accent : '#fff';
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.fillRect(a.x - r, a.y - r, r * 2, r * 2);
    ctx.strokeRect(a.x - r, a.y - r, r * 2, r * 2);
  }
  if (hover) {
    const a = toScreen(hover.x, hover.y);
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(a.x, a.y, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(a.x - 3, a.y);
    ctx.lineTo(a.x + 3, a.y);
    ctx.moveTo(a.x, a.y - 3);
    ctx.lineTo(a.x, a.y + 3);
    ctx.stroke();
  }
}

/** The same primitive painted in one flat color (used to fade objects out). */
function veil(p: Primitive, color: string): Primitive {
  switch (p.k) {
    case 'shape':
    case 'path':
      return { ...p, fill: p.fill.kind === 'none' ? p.fill : { kind: 'solid', color }, stroke: p.stroke ? { ...p.stroke, color } : null };
    case 'text':
      return { ...p, style: { ...p.style, color } };
    case 'image':
      return { k: 'shape', shape: 'rect', x: p.x, y: p.y, w: p.w, h: p.h, fill: { kind: 'solid', color }, stroke: null };
    default:
      return p;
  }
}

/** Objects the eraser is about to remove look faded: covered by their own silhouette in the canvas color. */
function drawFaded(ctx: CanvasRenderingContext2D, camera: Camera, o: ObjectRecord, ed: Editor, bg: string): void {
  const prims = ed.scene.primitives(o).map((p) => veil(p, bg));
  const z = camera.zoom;
  const c = camera.worldToScreen(o.x + o.w / 2, o.y + o.h / 2);
  ctx.save();
  ctx.translate(c.x, c.y);
  ctx.rotate(o.rotation);
  ctx.scale(z, z);
  ctx.translate(-o.w / 2, -o.h / 2);
  for (const p of prims) drawPrimitive(ctx, p, 0.72, z * camera.dpr, { images: NO_IMAGES, textLodPx: 0 });
  ctx.restore();
  ctx.globalAlpha = 1;
}

/** Selection chrome, hover, marquee and creation previews in screen space. */
export function drawOverlay(ctx: CanvasRenderingContext2D, camera: Camera, ed: Editor): void {
  syncAccent();
  const doc = ed.doc;
  const drag = ed.dragState;
  const sel = ed.selection;

  // hover
  const hover = ed.hover;
  if (hover && !sel.includes(hover) && !drag && !ed.scene.isFolded(hover)) {
    const o = doc.get(hover);
    if (o) outline(ctx, camera, o, accent, 1.5);
  }

  // per-object outlines in multi-selection
  if (sel.length > 1) {
    for (const id of sel) {
      const o = doc.get(id);
      if (o) outline(ctx, camera, o, accentFade(0.6), 1);
    }
  }

  // node editing of a bezier path replaces the box handles
  const pe = ed.pathEditState;
  if (pe) drawPathEdit(ctx, camera, pe.obj, pe.selected, pe.hover);

  // selection box + handles
  const box = pe ? null : ed.selectionBox();
  const moving = drag && (drag.kind === 'move' || drag.kind === 'rotate' || drag.kind === 'resize');
  if (box) {
    const single = sel.length === 1 ? doc.get(sel[0]) : undefined;
    if (single && (single.type === CoreTypes.line || single.type === CoreTypes.connector)) {
      outline(ctx, camera, single, accent, 2);
    } else {
      const c = boxScreenCorners(box, camera);
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.5;
      ctx.setLineDash(sel.length > 1 ? [4, 3] : []);
      ctx.beginPath();
      ctx.moveTo(c[0].x, c[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(c[i].x, c[i].y);
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (!moving) {
      const handles = ed.selectionHandles();
      const rot = handles.find((h) => h.id === 'rot');
      if (rot) {
        const top = localToWorld(box, { x: box.w / 2, y: 0 });
        const ts = camera.worldToScreen(top.x, top.y);
        ctx.strokeStyle = accent;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ts.x, ts.y);
        ctx.lineTo(rot.screen.x, rot.screen.y);
        ctx.stroke();
      }
      for (const h of handles) {
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = accent;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (h.id === 'rot' || h.id === 'p0' || h.id === 'p1') ctx.arc(h.screen.x, h.screen.y, HANDLE_SIZE / 2 + (h.id === 'rot' ? 1 : 2), 0, Math.PI * 2);
        else ctx.rect(h.screen.x - HANDLE_SIZE / 2, h.screen.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        ctx.fill();
        ctx.stroke();
      }
    }
    if (!moving) drawQuick(ctx, camera, ed);
    // lock badges
    for (const id of sel) {
      const o = doc.get(id);
      if (!o?.locked) continue;
      const b = boxBounds(o);
      const s = camera.worldToScreen(b.x + b.w, b.y);
      drawLock(ctx, s.x + 6, s.y - 18);
    }
  }

  drawKnobs(ctx, ed);

  // frame that will receive the moved selection
  if (drag?.kind === 'move' && drag.dropFrame) {
    const f = doc.get(drag.dropFrame);
    if (f) outline(ctx, camera, f, accent, 2.5);
  }

  // smart guides
  if ((drag?.kind === 'move' || drag?.kind === 'resize') && drag.guides.length) {
    ctx.strokeStyle = '#ec4899';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    for (const g of drag.guides) {
      const a = g.axis === 'x' ? camera.worldToScreen(g.at, g.from) : camera.worldToScreen(g.from, g.at);
      const b = g.axis === 'x' ? camera.worldToScreen(g.at, g.to) : camera.worldToScreen(g.to, g.at);
      ctx.beginPath();
      ctx.moveTo(Math.round(a.x) + 0.5, Math.round(a.y) + 0.5);
      ctx.lineTo(Math.round(b.x) + 0.5, Math.round(b.y) + 0.5);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }
  // equal spacing: the matching gaps with end ticks
  if (drag?.kind === 'move' && drag.gaps?.length) {
    ctx.strokeStyle = '#ec4899';
    ctx.lineWidth = 1;
    for (const g of drag.gaps) {
      const a = g.axis === 'x' ? camera.worldToScreen(g.from, g.at) : camera.worldToScreen(g.at, g.from);
      const b = g.axis === 'x' ? camera.worldToScreen(g.to, g.at) : camera.worldToScreen(g.at, g.to);
      const tick = (p: { x: number; y: number }) => (g.axis === 'x' ? [p.x, p.y - 4, p.x, p.y + 4] : [p.x - 4, p.y, p.x + 4, p.y]);
      ctx.beginPath();
      ctx.moveTo(Math.round(a.x) + 0.5, Math.round(a.y) + 0.5);
      ctx.lineTo(Math.round(b.x) + 0.5, Math.round(b.y) + 0.5);
      for (const p of [a, b]) {
        const [x0, y0, x1, y1] = tick(p);
        ctx.moveTo(Math.round(x0) + 0.5, Math.round(y0) + 0.5);
        ctx.lineTo(Math.round(x1) + 0.5, Math.round(y1) + 0.5);
      }
      ctx.stroke();
    }
  }

  // drawing in progress: the would-be object, rendered as it will look
  const ghost = ed.ghost;
  if (ghost) drawGhost(ctx, camera, ghost, ed);

  // pen tool: anchors, handles of the node being shaped, and the "close path" target
  const draft = ed.pathDraftState;
  if (draft) {
    const n = draft.nodes.length / 6;
    const last = draft.dragging >= 0 ? draft.dragging : n - 1;
    ctx.lineWidth = 1;
    for (const i of [last - 1, last]) {
      if (i < 0) continue;
      const b = i * 6;
      const a = camera.worldToScreen(draft.nodes[b], draft.nodes[b + 1]);
      for (const h of [2, 4]) {
        const hx = draft.nodes[b + h];
        const hy = draft.nodes[b + h + 1];
        if (hx === draft.nodes[b] && hy === draft.nodes[b + 1]) continue;
        const s = camera.worldToScreen(hx, hy);
        ctx.strokeStyle = accent;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
        ctx.fillStyle = accent;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    for (let i = 0; i < n; i++) {
      const a = camera.worldToScreen(draft.nodes[i * 6], draft.nodes[i * 6 + 1]);
      const closeTarget = i === 0 && ed.pathCanClose;
      const r = closeTarget ? 5 : 3.5;
      ctx.fillStyle = i === last || closeTarget ? accent : '#fff';
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.5;
      ctx.fillRect(a.x - r, a.y - r, r * 2, r * 2);
      ctx.strokeRect(a.x - r, a.y - r, r * 2, r * 2);
    }
  }


  // objects the eraser will remove on release
  const marked = ed.eraseMarked;
  if (marked.length) {
    const bg = ed.renderer.opts.background;
    for (const id of marked) {
      const o = doc.get(id);
      if (o) drawFaded(ctx, camera, o, ed, bg);
    }
  }

  // eraser cursor
  if (ed.currentTool.kind === 'eraser') {
    const p = camera.worldToScreen(ed.pointerWorld.x, ed.pointerWorld.y);
    ctx.beginPath();
    ctx.arc(p.x, p.y, ed.eraserRadius, 0, Math.PI * 2);
    ctx.fillStyle = drag?.kind === 'erase' ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.6)';
    ctx.fill();
    ctx.strokeStyle = drag?.kind === 'erase' ? '#ef4444' : '#475569';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  if (drag?.kind === 'lasso' && drag.pts.length >= 4) {
    ctx.beginPath();
    for (let i = 0; i < drag.pts.length; i += 2) {
      const s = camera.worldToScreen(drag.pts[i], drag.pts[i + 1]);
      if (i === 0) ctx.moveTo(s.x, s.y);
      else ctx.lineTo(s.x, s.y);
    }
    ctx.closePath();
    ctx.fillStyle = accentFade(0.08);
    ctx.fill();
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (drag?.kind === 'marquee') {
    const r = normalizeRect(drag.start.x, drag.start.y, drag.cur.x, drag.cur.y);
    const a = camera.worldToScreen(r.x, r.y);
    const w = r.w * camera.zoom;
    const h = r.h * camera.zoom;
    ctx.fillStyle = accentFade(0.08);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    ctx.fillRect(a.x, a.y, w, h);
    ctx.strokeRect(a.x + 0.5, a.y + 0.5, w, h);
  }

  if (drag?.kind === 'create' && !ghost) {
    const r = normalizeRect(drag.start.x, drag.start.y, drag.cur.x, drag.cur.y);
    const a = camera.worldToScreen(r.x, r.y);
    ctx.strokeStyle = accent;
    ctx.setLineDash([5, 4]);
    ctx.lineWidth = 1.5;
    const w = r.w * camera.zoom;
    const h = r.h * camera.zoom;
    if (drag.tool.objectType === CoreTypes.ellipse) {
      ctx.beginPath();
      ctx.ellipse(a.x + w / 2, a.y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else ctx.strokeRect(a.x, a.y, w, h);
    ctx.setLineDash([]);
  }

  if (drag?.kind === 'line' && !ghost) {
    const a = camera.worldToScreen(drag.start.x, drag.start.y);
    const b = camera.worldToScreen(drag.cur.x, drag.cur.y);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // connector targets
  const linking = ed.currentTool.kind === 'connector' || ed.currentTool.kind === 'line' || drag?.kind === 'endpoint' || (drag?.kind === 'quick' && drag.moved);
  if (linking) {
    const targets = [ed.bindTarget, ed.hover].filter((x): x is string => !!x);
    for (const id of new Set(targets)) {
      const o = doc.get(id);
      if (!o) continue;
      outline(ctx, camera, o, accent, 2);
      if (o.type === CoreTypes.connector || o.type === CoreTypes.line) continue;
      for (const [u, v] of [
        [0.5, 0],
        [1, 0.5],
        [0.5, 1],
        [0, 0.5],
      ]) {
        const w = localToWorld(o, { x: u * o.w, y: v * o.h });
        const s = camera.worldToScreen(w.x, w.y);
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = accent;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
    // snap point on the target frame / path
    const b = ed.bindPreview;
    if (b) {
      const s = camera.worldToScreen(b.point.x, b.point.y);
      ctx.fillStyle = accent;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
}

/**
 * "Connected shape" points around the selected shape: small dots; the one under the pointer becomes
 * an arrow, and the shape and arrow it would add are drawn faintly.
 */
function drawQuick(ctx: CanvasRenderingContext2D, camera: Camera, ed: Editor): void {
  const handles = ed.quickHandles();
  if (!handles.length) return;
  const hot = ed.quickHover;
  if (hot) {
    const pv = ed.quickPreview;
    if (pv?.arrow) drawGhost(ctx, camera, pv.arrow, ed);
    if (pv?.shape) drawGhost(ctx, camera, pv.shape, ed);
    else if (pv?.rect) {
      const a = camera.worldToScreen(pv.rect.x, pv.rect.y);
      ctx.strokeStyle = accentFade(0.7);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(a.x, a.y, pv.rect.w * camera.zoom, pv.rect.h * camera.zoom);
      ctx.setLineDash([]);
    }
  }
  const angle = { up: -Math.PI / 2, right: 0, down: Math.PI / 2, left: Math.PI };
  for (const h of handles) {
    const { x, y } = h.screen;
    ctx.beginPath();
    if (h.dir === hot) {
      ctx.arc(x, y, 10, 0, Math.PI * 2);
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle[h.dir]);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(-4.5, 0);
      ctx.lineTo(4.5, 0);
      ctx.moveTo(1, -3.5);
      ctx.lineTo(4.5, 0);
      ctx.lineTo(1, 3.5);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = accentFade(0.85);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }
}

/**
 * Branch knobs: a small ring just outside the shape an expanded branch leaves (a minus while the pointer
 * is on it); for collapsed branches a filled circle with the number of hidden shapes on a short stub.
 * Coloured like the branch.
 */
function drawKnobs(ctx: CanvasRenderingContext2D, ed: Editor): void {
  for (const k of ed.foldKnobs()) {
    const { x, y } = k.screen;
    if (k.stub) {
      ctx.strokeStyle = k.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(k.stub.x, k.stub.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    // a counter (something is folded there) even while other branches from that point are still open
    const counter = !!k.stub;
    ctx.beginPath();
    ctx.arc(x, y, k.r + (k.hot ? 1 : 0), 0, Math.PI * 2);
    ctx.fillStyle = counter ? k.color : '#ffffff';
    ctx.fill();
    ctx.strokeStyle = counter ? '#ffffff' : k.color;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    if (counter) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '600 11px system-ui, -apple-system, "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(k.count), x, y + 0.5);
    } else if (k.hot) {
      ctx.strokeStyle = k.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 3.5, y);
      ctx.lineTo(x + 3.5, y);
      ctx.stroke();
    }
  }
}

function drawLock(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = '#334155';
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x + 6, y + 6, 3.5, Math.PI, 0);
  ctx.stroke();
  ctx.fillRect(x + 1.5, y + 6, 9, 7);
}
